<script>
import MixedGrid from '@phoenix/mixins/components/mixedGrid/MixedGrid';
import Product from '../common/Product';
import MixedGridFilters from './MixedGridFilters';
import MixedGridImageItem from './MixedGridImageItem';
import MixedGridVideoItem from './MixedGridVideoItem';
import MixedGridPagePushItem from './MixedGridPagePushItem';
import MixedGridReversoItem from './MixedGridReversoItem';
import { map } from 'lodash-es';
import { computed } from 'vue';
import { useMixedGridItems } from '@maison/composables/mixedGridItems';

export default {
  components: {
    Product,
    MixedGridFilters,
    MixedGridImageItem,
    MixedGridVideoItem,
    MixedGridPagePushItem,
    MixedGridReversoItem,
  },

  mixins: [MixedGrid],

  provide() {
    return {
      reversoAllCounts: computed(() => this.reversoAllCounts),
      reversoFilterId: this.reversoFilterId,
    };
  },

  props: {
    allSortedItems: {
      type: Array,
      required: true,
    },
    cta: {
      type: Object,
      default: null,
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    customTags: {
      type: Array,
      default: () => [],
    },
    reversoFilterId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { getComponent } = useMixedGridItems();

    return { getComponent };
  },

  computed: {
    loadMoreClasses() {
      return [
        'products__filtered-buttons',
        map(this.maxPage, (page, size) => {
          const prefix = size !== 'xs' ? `${size}:` : '';
          const hidden = prefix ? 'hidden' : 'force-hidden'; // The 'hidden' class is not correctly imported in the SG right now, remove later
          return this.page >= page ? `${prefix}${hidden}` : `${prefix}grid`;
        }),
      ];
    },

    reversoAllCounts() {
      const activeFilters = { ...this.activeFilters };
      delete activeFilters[this.reversoFilterId];
      return this.filterProducts(this.columns, activeFilters).filter(({ template }) => template === 'product').length;
    },
  },

  methods: {
    initColumns() {
      this.columns = this.allSortedItems.map((item) => {
        if (item.template === 'product') {
          return {
            data: {
              ...item,
              enableSlider: true,
            },
            template: 'product',
            size: 1,
          };
        }
        return item;
      });

      const currentSize = {};
      for (const size of Object.keys(this.widths)) {
        currentSize[size] = 0;
      }

      for (const column of this.columns) {
        column.page = {};

        Object.keys(currentSize).forEach((size) => {
          let columnSize;
          if (['sm', 'xs'].includes(size) && column.template !== 'product') {
            columnSize = 2;
          } else {
            columnSize = column.size;
          }

          currentSize[size] += columnSize;
          column.page[size] = Math.ceil(currentSize[size] / this.itemsPerPage);
        });

        this.maxPage = { ...column.page };
      }
    },

    getDisplayedColumnClasses(column) {
      if (!this.shouldDisplayLoadMore) {
        return [];
      }

      return map(column.page, (page, size) => {
        const prefix = size !== 'xs' ? `${size}:` : '';
        return this.page < page ? `${prefix}hidden` : `${prefix}block`;
      });
    },

    toggleFiltersInMixedGridFilters(filterId, value) {
      this.$refs.mixedGridFilters.toggleFilter(filterId, value);
    },
  },
};
</script>

<template>
  <mixed-grid-filters
    v-if="displayFilter"
    ref="mixedGridFilters"
    :filters="filtersWithLocalizedPrices"
    :cta="cta"
    :empty-filters="emptyFilters"
    :number-products-by-filters="numberProductsByFilters"
    :products-count="filteredColumnsCount"
    :unique-id="uniqueId"
    :default-sort="defaultSort"
    :default-filters="activeFilters"
    :should-display-sellable="shouldDisplaySellable"
    :has-filters-in-url="hasFiltersInUrl"
    :is-dark-mode="isDarkMode"
    :unclearable-filters="unclearableFilters"
    :should-display-unclearable-filters="shouldDisplayUnclearableFilters"
    :custom-tags="customTags"
    @on-sort-change="onSortChange"
    @on-filters-changed="onFiltersChanged"
  />
  <div class="container section">
    <div class="products__grid">
      <div class="mixed-grid" data-cy="mixed-grid">
        <component
          :is="getComponent(column.template)"
          v-for="column in filteredColumns"
          :key="column.data.itemId"
          v-bind="column.data"
          :display-classes="getDisplayedColumnClasses(column)"
          data-cy="mixed-grid-item"
          :data-cy-item="column.template"
          :data-tracking="column.template === 'product' ? getDataTracking(column.data) : null"
          @on-toggle-filter="toggleFiltersInMixedGridFilters"
        >
        </component>
      </div>
    </div>
    <div v-if="shouldDisplayLoadMore" :class="loadMoreClasses">
      <button type="button" class="btn btn--default" data-cy="mixed-grid-load-more" @click="nextPage">
        <span class="btn__text">{{ $t('Button.loadMore') }}</span>
      </button>

      <button type="button" class="btn btn--default btn--negative" data-cy="mixed-grid-see-all" @click="seeAll">
        <span class="btn__text">{{ $t('Button.seeAll') }}</span>
      </button>
    </div>
  </div>
</template>
